import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "../components/Reveal";
import SectionNav from "../components/SectionNav";
import { PageHero, SectionHead, CtaBand, ImageBlock } from "../components/ui";
import { ApplicationForm } from "../components/forms";
import { IMAGES } from "../data/site";

const THEMES = ["Responsibility", "Infrastructure", "Energy transition", "Technical rigour", "Ownership", "Meaningful work"];

const CAREER_SECTIONS = [
    { id: "the-work", label: "The work" },
    { id: "open-positions", label: "Open positions" },
];

export default function Careers() {
    const [jobs, setJobs] = useState(null);
    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/jobs`)
            .then((res) => setJobs(res.data.jobs || []))
            .catch(() => setJobs([]));
    }, []);

    return (
        <>
            <PageHero
                eyebrow="Careers"
                lines={["Why join us?"]}
                sub="Because the work is real, the responsibility is real, and the infrastructure outlasts all of us."
                trail={[{ label: "Home", path: "/" }, { label: "Careers" }]}
                image={IMAGES.controlRoom}
                imageAlt="Engineers operating an industrial control room"
            />
            <SectionNav items={CAREER_SECTIONS} navKey="careers" />
            <section id="the-work" className="bg-paper">
                <div className="container-x grid gap-12 py-20 sm:py-24 lg:grid-cols-12 lg:py-28">
                    <div className="lg:col-span-4">
                        <Reveal><p className="eyebrow lg:sticky lg:top-36">The work</p></Reveal>
                    </div>
                    <div className="space-y-8 lg:col-span-8">
                        <Reveal>
                            <p className="font-display text-2xl font-medium leading-[1.35] text-ink sm:text-3xl">
                                We develop large-scale energy storage — infrastructure that
                                Europe's power system depends on.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="body-lg">
                                That carries responsibility: for capital, for communities, and for
                                the reliability of the grid. People who join AddiGen work on
                                complex projects with technical rigour, genuine ownership and a
                                direct line between their work and its impact.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>
            <section className="border-y border-line bg-white">
                <div className="container-x py-16 sm:py-20">
                    <div className="grid gap-px bg-line sm:grid-cols-2 lg:grid-cols-3">
                        {THEMES.map((t, i) => (
                            <Reveal key={t} delay={i * 0.05} className="bg-white">
                                <div className="flex h-full items-baseline justify-between bg-white px-6 py-8 sm:px-8">
                                    <p className="font-display text-xl font-semibold text-ink sm:text-2xl">{t}</p>
                                    <span className="font-mono text-[11px] text-ink-3">0{i + 1}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
            <section id="open-positions" className="bg-paper">
                <div className="container-x grid gap-12 py-20 sm:py-24 lg:grid-cols-12 lg:py-28">
                    <div className="lg:col-span-5">
                        <SectionHead eyebrow="Open positions" title="Positions are posted here as the platform grows." />
                        <div className="mt-10">
                            <ImageBlock
                                src={IMAGES.controlRoom2}
                                alt="An engineer monitoring operations in a control room"
                                caption="Control environments — where storage meets the grid"
                                ratio="aspect-[4/3]"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <Reveal>
                            <div className="border border-line bg-white">
                                <ul className="divide-y divide-line" data-testid="job-listings">
                                    {jobs === null && (
                                        <li className="px-8 py-10 body-sm">Loading positions…</li>
                                    )}
                                    {jobs !== null && jobs.length === 0 && (
                                        <li className="px-8 py-14 sm:px-12" data-testid="jobs-empty-state">
                                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue">Currently no open positions</p>
                                            <p className="body-lg mt-5 max-w-md">
                                                There are no vacancies at this time. New roles will be
                                                published here as the platform grows.
                                            </p>
                                            <p className="body-sm mt-6 text-ink-3">
                                                Each listing will show the job title, employment type,
                                                location and publication date, with a full description
                                                and application form.
                                            </p>
                                        </li>
                                    )}
                                    {jobs !== null && jobs.length > 0 && jobs.map((j) => (
                                        <li key={j.id} className="group px-8 py-8 sm:px-12">
                                            <p className="font-display text-xl font-bold text-ink">{j.title}</p>
                                            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-3">
                                                {j.type} · {j.location} · Published {j.date}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                        {jobs !== null && jobs.length > 0 && (
                            <Reveal delay={0.1}>
                                <div className="mt-10 border border-line bg-white p-8 sm:p-10">
                                    <h3 className="h3-compact">Apply</h3>
                                    <p className="body-sm mt-2">CV: PDF or Word only.</p>
                                    <div className="mt-8">
                                        <ApplicationForm jobId={String(jobs[0].id)} jobTitle={jobs[0].title} />
                                    </div>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>
            <CtaBand cta="Get in touch" />
        </>
    );
}
